<template>
  <div class="extraction-template-option-list">
    <div class="subtitle-2 font-weight-light">Options</div>
    <icon-button
      size="x-small"
      class="etol-small-button"
      color="primary"
      icon-color="white"
      icon="add"
      tabindex="-1"
      :disabled="disabled"
      @click="addOption"
    />
    <template v-for="option in optionList">
      <div :key="`item:${option.id}`" class="etol-item">
        <v-radio v-if="displayRadio" disabled readonly hide-details />
        <v-simple-checkbox
          v-else-if="displayCheckbox"
          class="mr-2"
          disabled
          readonly
        />
        <v-text-field
          ref="options"
          class="text-body-2"
          :disabled="disabled"
          :value="option.label"
          :error-messages="getErrorMessage(option)"
          @input="clearErrorMessage(option)"
          @change="updateOption(option, $event)"
        />
      </div>
      <icon-button
        :key="`remove-btn:${option.id}`"
        class="etol-small-button"
        size="x-small"
        color="error"
        icon-color="white"
        icon="remove"
        tabindex="-1"
        :disabled="disabled"
        @click="removeOption(option)"
      />
    </template>
    <v-row no-gutters>
      <v-checkbox
        :input-value="allowOther"
        :disabled="disabled"
        hide-details
        class="flex-grow-1 text-body-2"
        label="Include ‘Other’ Option"
        @change="$emit('input-allowOther', $event)"
      />
    </v-row>
  </div>
</template>

<script>
import { typeHelpers } from '@/modules';
import IconButton from '../Common/IconButton.vue';
import { v4 as uuidv4 } from 'uuid';

export default {
  name: 'ExtractionFieldSingleSelect',
  components: { IconButton },
  props: {
    options: {
      type: Array,
      required: true,
      validator: (val) => typeHelpers.isArrayOfType(val, 'string'),
    },
    allowOther: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dataType: {
      type: String,
      required: true,
    },
  },
  emits: ['input-options', 'input-allowOther'],
  data: () => ({
    optionList: [],
    modifiedOption: null,
  }),
  computed: {
    displayRadio() {
      return this.dataType === 'DataExtractionFieldSingleSelect';
    },
    displayCheckbox() {
      return this.dataType === 'DataExtractionFieldMultipleSelect';
    },
  },
  created() {
    this.optionList = this.options.map((label) =>
      this.createOption(label)
    );
  },
  mounted() {
    this.focusDefaultOption();
  },
  methods: {
    createOption(label = '') {
      return {
        id: uuidv4(),
        label: label,
      };
    },
    addOption() {
      this.optionList.push(this.createOption());
      this.$nextTick(this.focusDefaultOption);
    },
    updateOption(option, newLabel) {
      option.label = newLabel;
      if (option.id === this.modifiedOption?.id) {
        this.modifiedOption = null;
      }
      this.emitIfValid();
    },
    removeOption(toRemove) {
      this.optionList = this.optionList.filter(
        (x) => x.id !== toRemove.id
      );
      this.emitIfValid();
    },
    emitIfValid() {
      for (const option of this.optionList) {
        if (this.getErrorMessage(option)) {
          return;
        }
      }
      this.$emit(
        'input-options',
        this.optionList.map((option) => option.label)
      );
    },
    getErrorMessage(option) {
      const { label, id } = option;

      if (id === this.modifiedOption?.id) {
        return '';
      }

      if (label.length === 0) {
        return 'Option cannot be empty';
      }

      const thisOptionIndex = this.optionList.findIndex(
        (x) => x.id === id
      );
      for (let i = 0; i < this.optionList.length; i++) {
        if (i === thisOptionIndex) {
          continue;
        }
        const otherOption = this.optionList[i];
        if (otherOption.label === label && thisOptionIndex > i) {
          return 'Options must be unique';
        }
      }

      return '';
    },
    focusDefaultOption() {
      const firstEmptyIdx = this.optionList.findIndex(
        (option) => option.label.length === 0
      );
      const targetOptionRef = this.$refs.options[
        firstEmptyIdx !== -1 ? firstEmptyIdx : 0
      ];
      if (targetOptionRef) {
        targetOptionRef.focus();
      }
    },
    clearErrorMessage(option) {
      this.modifiedOption = option;
    },
  },
};
</script>

<style scoped lang="scss">
$icon-size: 20px;
.extraction-template-option-list {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
}

.etol-item {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  justify-content: start;
}

.etol-small-button {
  height: $icon-size;
  width: $icon-size;
}
</style>
